import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtDate } from "../SuperHeader";
import { useAdminFetch, TabState } from "../useAdminFetch";
import Pager from "../Pager";

const STATES = { ai_active: "AI handling", human_active: "Human handling", waiting: "Waiting for human", closed: "Closed" };

function Thread({ cid, conv, onClose }) {
  const { data, error, loading } = useAdminFetch(`/admin/companies/${cid}/conversations/${conv.id}/messages`);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-2xl" data-testid="conversation-dialog">
        <DialogHeader><DialogTitle>{conv.contact_name || conv.contact_phone} · {STATES[conv.state] || conv.state}</DialogTitle></DialogHeader>
        <p className="text-[11px] text-slate-400">Viewing is recorded in the activity log.</p>
        <div className="max-h-[60vh] overflow-y-auto space-y-2 bg-slate-50 rounded-lg p-3">
          <TabState loading={loading} error={error} empty={data?.messages.length === 0} emptyText="No messages." testid="thread">
            {(data?.messages || []).map((m) => (
              <div key={m.id} className={`flex ${m.direction === "inbound" ? "justify-start" : "justify-end"}`}>
                <div className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm border ${m.direction === "inbound" ? "bg-white border-slate-200" : m.sender === "ai" ? "bg-indigo-50 border-indigo-100" : "bg-emerald-50 border-emerald-100"}`}>
                  <div className="text-[10px] uppercase text-slate-400 mb-0.5">{m.sender} · {fmtDate(m.created_at)}</div>{m.body}
                </div>
              </div>))}
          </TabState>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ConversationsTab({ cid }) {
  const [state, setState] = useState(null);
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(null);
  useEffect(() => setPage(1), [state]);
  const { data, error, loading } = useAdminFetch(`/admin/companies/${cid}/conversations`, { state: state || undefined, page });
  return (
    <Card className="p-4 border-slate-200 space-y-3" data-testid="conversations-tab">
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setState(null)} className={`px-3 py-1 rounded-full text-xs border ${!state ? "bg-indigo-600 text-white border-indigo-600" : "bg-white"}`} data-testid="conv-filter-all">All</button>
        {Object.entries(STATES).map(([k, l]) => (
          <button key={k} onClick={() => setState(k)} data-testid={`conv-filter-${k}`}
            className={`px-3 py-1 rounded-full text-xs border ${state === k ? "bg-indigo-600 text-white border-indigo-600" : "bg-white"}`}>{l} ({data?.by_state?.[k] || 0})</button>
        ))}
      </div>
      <TabState loading={loading && !data} error={error} empty={data?.items.length === 0} emptyText="No conversations." testid="conversations">
        <div className="divide-y divide-slate-100">
          {(data?.items || []).map((c) => (
            <button key={c.id} onClick={() => setOpen(c)} className="w-full text-left p-3 hover:bg-slate-50 flex items-center justify-between gap-3" data-testid={`conv-row-${c.id}`}>
              <div className="min-w-0"><div className="font-medium text-sm">{c.contact_name || c.contact_phone || "Unknown"}</div><div className="text-xs text-slate-500 truncate">{c.last_message_preview}</div></div>
              <div className="text-right shrink-0"><Badge variant="outline">{STATES[c.state] || c.state}</Badge><div className="text-[11px] text-slate-400 mt-1">{fmtDate(c.last_message_at)}</div></div>
            </button>))}
        </div>
      </TabState>
      {data && data.total > 0 && <Pager page={data.page} pages={data.pages} total={data.total} onPage={setPage} testid="conv-pager" />}
      {open && <Thread cid={cid} conv={open} onClose={() => setOpen(null)} />}
    </Card>
  );
}
